import { useQuery } from "@tanstack/react-query";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { useState } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RefreshCw, Search, AlertCircle, ShieldAlert } from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ViewToggle from "@/components/ViewToggle";
import { formatDistanceToNow } from "date-fns";
import { useAppIconLookup } from "@/hooks/useAppIconLookup";
import { isAppUpdate, extractAppName } from "@/utils/appIcons";
import { AppIcon } from "@/components/applications/AppIcon";
import { GlassCard } from "@/components/ui/glass-card";

interface CVEDetail {
  id: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  score: number;
  description?: string;
}

interface AppleUpdate {
  name: string;
  version: string;
  build: string;
  released: string;
  security: boolean;
  stability: boolean;
  requires: string;
  notes_url: string;
  compatibility: {
    minimum: string;
    maximum?: string;
  };
  security_notes?: string[];
  actively_exploited?: boolean;
  cve_ids?: string[];
  cve_details?: CVEDetail[];
  os_type: string;
}

// Helper to get the branded variant for each OS type
const getBrandedVariant = (osType: string): 'macos-branded' | 'ios-branded' | 'ipados-branded' | 'watchos-branded' | undefined => {
  switch (osType.toLowerCase()) {
    case 'macos':
      return 'macos-branded';
    case 'ios':
      return 'ios-branded';
    case 'ipados':
      return 'ipados-branded';
    case 'watchos':
      return 'watchos-branded';
    default:
      return undefined;
  }
};

export function AvailableUpdates() {
  const [osType, setOsType] = useState<string>("all");
  const [selectedVersionType, setSelectedVersionType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [view, setView] = useState<"grid" | "list">("list");
  const { getAppIcon } = useAppIconLookup();

  const { data: updatesData, isLoading, error, refetch } = useQuery({
    queryKey: ["apple-updates", osType, selectedVersionType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (osType !== 'all') params.append('os_type', osType);
      if (selectedVersionType === 'security') params.append('security', 'true');
      if (selectedVersionType === 'exploited') params.append('actively_exploited', 'true');
      
      const queryString = params.toString();
      const url = queryString ? `get-apple-updates?${queryString}` : 'get-apple-updates';
      
      const { data, error } = await supabase.functions.invoke(url);

      if (error) {
        console.error("Error fetching updates:", error);
        throw error;
      }

      return data?.updates || [];
    },
  });

  const updates = updatesData as AppleUpdate[] || [];

  const { data: lastFetchTime } = useQuery({
    queryKey: ["last-apple-update-fetch"],
    queryFn: async () => {
      const { data } = await supabase
        .from('apple_updates')
        .select('last_fetched')
        .order('last_fetched', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.last_fetched ? new Date(data.last_fetched) : null;
    },
  });

  const refreshUpdates = async () => {
    setIsRefreshing(true);
    try {
      console.log('Refreshing Apple updates...');
      
      const { error: fetchError } = await supabase.functions.invoke('fetch-apple-updates-rss');

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      // Immediately refetch to show updates
      await refetch();
      setIsRefreshing(false);
      
      toast.success("Updates refreshed");
      toast.info("CVE enrichment running in background...");

      // Fire-and-forget: start async CVE enrichment
      supabase.functions.invoke('backfill-cve-details', {
        body: { mode: 'async' }
      }).catch(err => {
        console.warn("Failed to start CVE enrichment:", err);
      });

      // Poll for progressive updates
      let pollCount = 0;
      const maxPolls = 12; // 60 seconds total
      const pollInterval = setInterval(async () => {
        pollCount++;
        await refetch();
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          console.log('CVE enrichment polling completed');
        }
      }, 5000);

    } catch (err) {
      console.error("Failed to refresh Apple updates:", err);
      toast.error("Failed to refresh updates.");
      setIsRefreshing(false);
    }
  };

  const filteredUpdates = updates?.filter(update => {
    const matchesSearch = searchQuery === "" || 
      update.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      update.version.toLowerCase().includes(searchQuery.toLowerCase()) ||
      update.build.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (update.cve_ids && update.cve_ids.some(cve => cve.toLowerCase().includes(searchQuery.toLowerCase())));
    
    if (!matchesSearch) return false;

    switch (selectedVersionType) {
      case "security":
        return update.security;
      case "stability":
        return update.stability;
      case "exploited":
        return update.actively_exploited;
      case "all":
        return true;
      default:
        return true;
    }
  });

  const renderSecurityBadge = (isSecurityUpdate: boolean) => {
    return isSecurityUpdate ? (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-200">Security</Badge>
    ) : null;
  };

  const renderStabilityBadge = (isStabilityUpdate: boolean) => {
    return isStabilityUpdate ? (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Stability</Badge>
    ) : null;
  };

  const renderExploitedBadge = (isExploited: boolean | undefined) => {
    return isExploited ? (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        <span>Actively Exploited</span>
      </Badge>
    ) : null;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-600 text-white hover:bg-red-700 border-red-700';
      case 'HIGH':
        return 'bg-orange-500 text-white hover:bg-orange-600 border-orange-600';
      case 'MEDIUM':
        return 'bg-yellow-500 text-black hover:bg-yellow-600 border-yellow-600';
      case 'LOW':
        return 'bg-blue-500 text-white hover:bg-blue-600 border-blue-600';
      default:
        return 'bg-muted text-muted-foreground hover:bg-muted/80 border-border';
    }
  };

  const renderCVEWithSeverity = (cveDetail: CVEDetail) => {
    return (
      <TooltipProvider key={cveDetail.id}>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={`https://nvd.nist.gov/vuln/detail/${cveDetail.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Badge 
                className={`cursor-pointer ${getSeverityColor(cveDetail.severity)}`}
              >
                <ShieldAlert className="h-3 w-3 mr-1" />
                {cveDetail.id}
                <span className="ml-1 text-xs font-semibold">({cveDetail.severity})</span>
              </Badge>
            </a>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1 max-w-xs">
              <p className="font-semibold">{cveDetail.id}</p>
              <p className="text-sm">Severity: <span className="font-semibold">{cveDetail.severity}</span></p>
              <p className="text-sm">CVSS Score: <span className="font-semibold">{cveDetail.score.toFixed(1)}</span></p>
              {cveDetail.description && (
                <p className="text-xs text-muted-foreground mt-2">{cveDetail.description}</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center space-x-2 w-full md:w-auto">
          <Select value={osType} onValueChange={setOsType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="macOS">macOS</SelectItem>
              <SelectItem value="iOS">iOS</SelectItem>
              <SelectItem value="iPadOS">iPadOS</SelectItem>
              <SelectItem value="watchOS">watchOS</SelectItem>
              <SelectItem value="tvOS">tvOS</SelectItem>
              <SelectItem value="visionOS">visionOS</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={selectedVersionType}
            onValueChange={setSelectedVersionType}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Updates</SelectItem>
              <SelectItem value="security">Security Updates</SelectItem>
              <SelectItem value="stability">Stability Updates</SelectItem>
              <SelectItem value="exploited">Actively Exploited</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={refreshUpdates}
              disabled={isRefreshing}
              title="Fetch latest Apple updates"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            {lastFetchTime && (
              <span className="text-xs text-muted-foreground">
                Last updated: {formatDistanceToNow(lastFetchTime, { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search updates or CVEs..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {view === "list" ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Platform</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Build</TableHead>
                <TableHead>Released</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>CVE IDs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading || isRefreshing ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Progress value={40} className="w-40 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {isRefreshing ? "Refreshing updates..." : "Loading updates..."}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredUpdates && filteredUpdates.length > 0 ? (
                filteredUpdates.map((update, index) => (
                  <TableRow key={index} className={`hover:bg-muted/50 ${update.actively_exploited ? "bg-yellow-50" : ""}`}>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        {isAppUpdate(update.name) ? (
                          <>
                            <AppIcon 
                              iconSrc={getAppIcon(extractAppName(update.name) || update.name)}
                              appName={extractAppName(update.name) || update.name}
                              className="h-4 w-4"
                            />
                            <span>{extractAppName(update.name)}</span>
                          </>
                        ) : (
                          <>
                            <AppleDeviceIcon 
                              osType={update.os_type} 
                              variant={getBrandedVariant(update.os_type)}
                              className="h-4 w-4" 
                            />
                            <span>{update.os_type}</span>
                          </>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <a 
                        href={update.notes_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {update.name}
                      </a>
                    </TableCell>
                    <TableCell>{update.version}</TableCell>
                    <TableCell>{update.build}</TableCell>
                    <TableCell>{formatDate(update.released)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {renderSecurityBadge(update.security)}
                        {renderStabilityBadge(update.stability)}
                        {renderExploitedBadge(update.actively_exploited)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {update.cve_details && update.cve_details.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {update.cve_details
                            .sort((a, b) => b.score - a.score)
                            .slice(0, 3)
                            .map((cveDetail) => (
                              <div key={cveDetail.id}>
                                {renderCVEWithSeverity(cveDetail)}
                              </div>
                            ))}
                          {update.cve_details.length > 3 && (
                            <Badge variant="secondary" className="text-xs w-fit">
                              +{update.cve_details.length - 3} more
                            </Badge>
                          )}
                          {update.cve_details.filter(c => c.severity === 'CRITICAL').length > 0 && (
                            <span className="text-xs text-red-600 font-semibold mt-1">
                              {update.cve_details.filter(c => c.severity === 'CRITICAL').length} Critical
                            </span>
                          )}
                        </div>
                      ) : update.cve_ids && update.cve_ids.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {update.cve_ids.slice(0, 2).map((cve, i) => (
                            <a
                              key={i}
                              href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Badge variant="outline" className="cursor-pointer hover:bg-red-50">
                                <ShieldAlert className="h-3 w-3 mr-1 text-red-500" />
                                {cve}
                              </Badge>
                            </a>
                          ))}
                          {update.cve_ids.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{update.cve_ids.length - 2} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">None reported</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    No updates found matching your criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        isLoading || isRefreshing ? (
          <div className="flex flex-col items-center justify-center h-32">
            <Progress value={40} className="w-56 mb-2" />
            <p className="text-sm text-muted-foreground">
              {isRefreshing ? "Refreshing updates..." : "Loading updates..."}
            </p>
          </div>
        ) : filteredUpdates && filteredUpdates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUpdates.map((update, index) => (
              <GlassCard 
                key={index} 
                hover
                className={`p-4 ${update.actively_exploited ? "border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30" : ""}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="flex items-center gap-1">
                      {isAppUpdate(update.name) ? (
                        <>
                          <AppIcon 
                            iconSrc={getAppIcon(extractAppName(update.name) || update.name)}
                            appName={extractAppName(update.name) || update.name}
                            className="h-4 w-4"
                          />
                          <span>{extractAppName(update.name)}</span>
                        </>
                      ) : (
                        <>
                          <AppleDeviceIcon 
                            osType={update.os_type} 
                            variant={getBrandedVariant(update.os_type)}
                            className="h-4 w-4" 
                          />
                          <span>{update.os_type}</span>
                        </>
                      )}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {renderSecurityBadge(update.security)}
                    {renderStabilityBadge(update.stability)}
                  </div>
                </div>

                <h3 className="font-medium mb-2">
                  <a 
                    href={update.notes_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {update.name}
                  </a>
                </h3>

                {update.actively_exploited && (
                  <div className="mb-2">
                    {renderExploitedBadge(update.actively_exploited)}
                  </div>
                )}

                <div className="text-sm text-muted-foreground mb-3">
                  <div>Version: {update.version} (Build {update.build})</div>
                  <div>Released: {formatDate(update.released)}</div>
                </div>

                {update.cve_details && update.cve_details.length > 0 ? (
                  <div className="mt-2 mb-2">
                    <p className="text-xs font-semibold flex items-center gap-1 mb-2">
                      <ShieldAlert className="h-3 w-3 text-red-500" />
                      Security Vulnerabilities:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {update.cve_details
                        .sort((a, b) => b.score - a.score)
                        .map((cveDetail) => (
                          <div key={cveDetail.id}>
                            {renderCVEWithSeverity(cveDetail)}
                          </div>
                        ))}
                    </div>
                    {update.cve_details.filter(c => c.severity === 'CRITICAL').length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="text-red-600 font-semibold">
                          ⚠️ {update.cve_details.filter(c => c.severity === 'CRITICAL').length} Critical Vulnerabilities
                        </span>
                      </div>
                    )}
                  </div>
                ) : update.cve_ids && update.cve_ids.length > 0 ? (
                  <div className="mt-2 mb-2">
                    <p className="text-xs font-semibold flex items-center gap-1 mb-2">
                      <ShieldAlert className="h-3 w-3 text-red-500" />
                      CVE IDs:
                    </p>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {update.cve_ids.map((cve, i) => (
                        <a
                          key={i}
                          href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-red-50">
                            {cve}
                          </Badge>
                        </a>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      Severity details pending enrichment...
                    </p>
                  </div>
                ) : null}

                {update.security_notes && update.security_notes.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p className="font-semibold">Security notes:</p>
                    <ul className="list-disc list-inside">
                      {update.security_notes.slice(0, 2).map((note, i) => (
                        <li key={i} className="truncate">{note}</li>
                      ))}
                      {update.security_notes.length > 2 && (
                        <li className="text-blue-600">
                          <a 
                            href={update.notes_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            +{update.security_notes.length - 2} more...
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </GlassCard>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No updates found matching your criteria.</p>
          </div>
        )
      )}
    </div>
  );
}
